/**
 * Functionality for the HM Tab block.
 *
 * Adapted from the code provided on https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/tab_role
 */

/**
 * Change to tabs.
 *
 * @param {HTMLElement} tab Tab element.
 */
function activateTab( tab ) {
	const blockElement = tab.closest( '.hm-tabs' );
	blockElement.classList.add( 'hm-tabs--focused' );

	const handleClickOutside = ( event ) => {
		if ( event.target !== blockElement && ! blockElement.contains( event.target ) ) {
			blockElement.classList.remove( 'hm-tabs--focused' );
			document.removeEventListener( 'click', handleClickOutside );
		}
	};

	// Skip activating if tab is already selected.
	if ( tab.getAttribute( 'aria-selected' ) === 'true' ) {
		// But clicking on an active tab, should focus container.
		document.addEventListener( 'click', handleClickOutside );
		return;
	}

	const panel = document.getElementById( tab.getAttribute( 'aria-controls' ) );
	const tabPanels = blockElement.querySelectorAll( '[role="tabpanel"]' );

	// Remove all current selected tabs
	blockElement
		.querySelectorAll( '[role="tab"][aria-selected="true"]' )
		.forEach( t => {
			t.setAttribute( 'aria-selected', false );
			t.setAttribute( 'tabindex', -1 );
			t.classList.remove( 'hm-tabs__nav-button--active' );
		} );

	// Set this tab as selected
	tab.setAttribute( 'aria-selected', true );
	tab.setAttribute( 'tabindex', 0 );
	tab.classList.add( 'hm-tabs__nav-button--active' );

	// Hide all tab panels
	tabPanels.forEach( p => {
		if ( p.getAttribute( 'id' ) === panel.getAttribute( 'id' ) ) {
			p.removeAttribute( 'hidden' );
		} else {
			p.setAttribute( 'hidden', true );
		}
	} );

	// Unfocus after switching tabs.
	blockElement.classList.remove( 'hm-tabs--focused' );
}

/**
 * Handle click event on tab.
 *
 * @param {object} e Event
 */
function handleTabClick( e ) {
	activateTab( e.target );
}

/**
 * Handle key navigation on tab element.
 *
 * @param {object} e Event
 */
function handleKeydown( e ) {
	if ( ! ( e.keyCode === 39 || e.keyCode === 37 ) ) {
		return;
	}

	const blockElement = e.target.closest( '.hm-tabs' );
	const tab = blockElement.querySelector( '[role="tab"][aria-selected="true"]' );
	const tabs = blockElement.querySelectorAll( '[role="tab"]' );

	const currentTabIndex = Array.from( tabs ).findIndex( t => tab.getAttribute( 'id' ) === t.getAttribute( 'id' ) );

	// Work out the next tab.
	let nextTabIndex = e.keyCode === 37 ? currentTabIndex - 1 : currentTabIndex + 1;
	nextTabIndex = Math.min( Math.max( nextTabIndex, 0 ), tabs.length );

	if ( nextTabIndex !== currentTabIndex ) {
		activateTab( tabs[ nextTabIndex ] );
		tabs[ currentTabIndex ].blur();
		tabs[ nextTabIndex ].focus();
	}
}

/**
 * Initialize tab block.
 *
 * Ensure initial state is correct.
 *
 * @param {HTMLElement} blockElement Tab block.
 */
const initTabBlock = ( blockElement ) => {
	const tabs = blockElement.querySelectorAll( '[role="tab"]' );
	const tabList = blockElement.querySelector( '[role="tablist"]' );

	// Add a click event handler to each tab
	tabs.forEach( tab => {
		const panel = document.getElementById( tab.getAttribute( 'aria-controls' ) );
		const isActive = tab.classList.contains( 'hm-tabs__nav-button--active' );

		if ( isActive ) {
			panel.removeAttribute( 'hidden' );
		} else {
			panel.setAttribute( 'hidden', true );
		}

		tab.addEventListener( 'click', handleTabClick );
	} );

	tabList.addEventListener( 'keydown', handleKeydown );
};

/**
 * Kick it all off.
 */
function bootstrap() {
	document.querySelectorAll( '.hm-tabs' )
		.forEach( el => initTabBlock( el ) );
}

document.addEventListener( 'DOMContentLoaded', bootstrap );
